import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { ApiPaymentService } from '@modules/private/payment/data/services/api/api-payment.service'
import { LeaseLedger } from '../../interfaces/lease-ledger.interface'

/**
 * Libro mayor del arrendamiento abierto (`GET /payments/ledger/:leaseId`). Una sola fuente para todas
 * las piezas de la pestaña Exigibilidad y para las lentes de Cobranza. No cuelga de
 * `LeaseDetailService.data`: recargar el lease entero destruye la pestaña (§8.2).
 */
@Injectable({ providedIn: 'root' })
export class LeaseLedgerService {
  private ledgerSubject = new BehaviorSubject<LeaseLedger | null>(null)
  public ledger$ = this.ledgerSubject.asObservable()

  leaseId = ''
  loading = false
  error = ''

  constructor(private apiPaymentService: ApiPaymentService) {}

  get current(): LeaseLedger | null {
    return this.ledgerSubject.getValue()
  }

  /** Carga (o recarga) el ledger del lease indicado. Siempre pega al backend: no hay caché. */
  load(leaseId: string): void {
    if (!leaseId) return
    //* Solo al CAMBIAR de lease ya cargado se emite null (para no pintar el ledger ajeno); en la primera carga
    //* el BehaviorSubject ya vale null y un segundo null solo ensuciaría a los suscriptores.
    if (this.leaseId && leaseId !== this.leaseId) this.ledgerSubject.next(null)
    this.leaseId = leaseId
    this.loading = true
    this.error = ''
    this.apiPaymentService.getLedger(leaseId).subscribe((r) => {
      this.loading = false
      if (r.error) {
        this.error = r.msg || 'No se pudo leer el libro mayor'
        return
      }
      this.ledgerSubject.next(r.data)
    })
  }

  /** Tras cada acción que mueva dinero: vuelve a pedir el ledger sin tocar `GET /leases/:id`. */
  refresh(): void {
    this.load(this.leaseId)
  }

  /** Al salir de la pestaña: el ledger del lease A no debe pintarse un instante en el lease B. */
  clear(): void {
    this.leaseId = ''
    this.error = ''
    this.ledgerSubject.next(null)
  }
}
